import { enumIsfState } from '@data-layer/enums/isf';
import { enumPaymentState } from '@data-layer/enums/payments';
import { enumCompanyPaymentType } from '@data-layer/enums/companies';
import { ManagementIsfModel } from '@data-layer/models/management';
import { BondModel, BondRequestModel, ShipmentModel } from '@data-layer/models/common';
import { enumBondRequestState } from '@data-layer/enums/common';
import { find } from 'lodash-es';
import { IsfEstimatedInfoModel, IsfModel } from '@data-layer/models/isf';
import { EntityModel } from '@data-layer/models/entities';
import { enumEntityByKey, enumEntityModelCode } from '@data-layer/enums/entities';
import { InvoiceModel } from '@data-layer/models/entry-summaries';
import { UtilsHelper } from '@helpers/utils-helper';

export interface IsfSameAsParent {
  code: enumEntityModelCode;
  entity: EntityModel;
}

export class IsfHelper {
  public static isCanEdit(state: enumIsfState, paymentState: enumPaymentState, userPaymentType: enumCompanyPaymentType) {
    const isState = [
      enumIsfState.NOT_SENT,
      enumIsfState.NOT_VALID_AT_PROVIDER,
      enumIsfState.NO_BILL_ON_FILE,
      enumIsfState.BILL_TYPE_NOT_MATCHED,
      enumIsfState.REJECTED_CBP,
      enumIsfState.CUSTOM,
    ].includes(state);

    const isPaymentState = [
      enumPaymentState.NOT_VALIDATED,
      enumPaymentState.PENDING,
      enumPaymentState.CREATED,
      enumPaymentState.CONFIRMED,
      enumPaymentState.FAILED,
      enumPaymentState.MULTI_FAILED,
      enumPaymentState.OUTDATED,
    ].includes(paymentState);

    return (
      (isState && !(isPaymentState && userPaymentType === enumCompanyPaymentType.INSTANT)) ||
      (isState && paymentState === enumPaymentState.PENDING && userPaymentType === enumCompanyPaymentType.MONTHLY)
    );
  }

  public static canBondRequestEdit(state: enumIsfState): boolean {
    const isState = [enumIsfState.NO_BILL_ON_FILE, enumIsfState.BILL_TYPE_NOT_MATCHED].includes(state);

    return !isState;
  }

  /**
   * Sent = any state past NOT_SENT that is not DELETED. An undefined state counts as not sent.
   */
  public static isSent(state: enumIsfState): boolean {
    return state > enumIsfState.NOT_SENT && state !== enumIsfState.DELETED;
  }

  public static getShipment(isf: IsfModel | ManagementIsfModel): ShipmentModel {
    if (!isf) {
      return null;
    }
    return isf.shipment || null;
  }

  public static getFullShipment(isf: IsfModel): ShipmentModel {
    if (!isf) {
      return null;
    }
    return isf.shipment || null;
  }

  public static getIsfShipmentEntityByCode(isf: IsfModel, code: enumEntityModelCode): EntityModel {
    return (find(isf.shipment, { entityCode: code }) as EntityModel) || null;
  }

  public static getIsfShipmentEntityById(isf: IsfModel | ManagementIsfModel, keyName: enumEntityByKey): EntityModel {
    if (keyName === enumEntityByKey.IMPORTER) {
      return isf.importer;
    } else {
      return (find(isf.shipment, { id: isf?.shipment?.[keyName] }) as EntityModel) || null;
    }
  }

  /**
   * #13536: a party "same as" another one is the SAME entity row (shipment.<slot>Id points at it),
   * so the relation is decided by id — never by the party's own entityCode.
   * Returns the slot code of the first parent (in the given precedence order) the entity is shared with.
   */
  public static getSameAsParentCode(entity: EntityModel, parents: IsfSameAsParent[]): enumEntityModelCode {
    if (!entity || !(entity.id > 0)) {
      return null;
    }

    const parent: IsfSameAsParent = parents.find((item: IsfSameAsParent) => !!item.entity && item.entity.id === entity.id);

    return parent ? parent.code : null;
  }

  public static getIsfManufacturerEntityList(isf: IsfModel | ManagementIsfModel): EntityModel[] {
    return (isf?.shipment?.invoices || []).reduce((acc: EntityModel[], current: InvoiceModel) => {
      return !!current.manufacturer
        ? [
            ...acc,
            {
              ...current.manufacturer,
              invoiceId: current.id,
            },
          ]
        : [...acc];
    }, []);
  }

  public static getIsfInvoicesList(isf: IsfModel | ManagementIsfModel): InvoiceModel[] {
    // group hts by manufacturer

    const manufacturerIds: number[] = UtilsHelper.uniq<number>(
      (isf?.shipment?.invoices || []).map((item: InvoiceModel) => item.manufacturerId),
    );
    const invoices = [];

    manufacturerIds.forEach((manufacturerId) => {
      const manufacturerInvoices = isf.shipment.invoices.filter((invoice) => invoice.manufacturerId === manufacturerId);
      const lines = (manufacturerInvoices || []).reduce((acc: InvoiceModel[], current: InvoiceModel) => {
        return [...acc, ...current.lines];
      }, []);

      invoices.push({ ...isf.shipment.invoices.find((invoice) => invoice.manufacturerId === manufacturerId), lines });
    });

    return invoices;
  }

  public static getBond(isf: ManagementIsfModel | IsfModel): BondModel {
    if (!isf) {
      return null;
    }
    return isf.bondRequest && isf.bondRequest.state !== enumBondRequestState.COMPLETED ? null : isf.bondInfo || null;
  }

  public static getBondRequest(isf: ManagementIsfModel | IsfModel): BondRequestModel {
    if (!isf) {
      return null;
    }
    return isf.bondRequest && isf.bondRequest.state !== enumBondRequestState.COMPLETED ? isf.bondRequest : null;
  }

  public static getEstimatedInformation(isf: IsfModel | ManagementIsfModel): IsfEstimatedInfoModel {
    if (!isf) {
      return null;
    }
    return isf.estimatedInformation || null;
  }

  public static getFullEstimatedInformation(isf: IsfModel): IsfEstimatedInfoModel {
    if (!isf) {
      return null;
    }
    return isf.estimatedInformation || null;
  }
}
